import type { BillingAccount, BillingEstimate, BillingHistoryEntry } from '~/gql/billing/billing'
import { useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GQL_BILLING_ACCOUNT, GQL_BILLING_ESTIMATE, GQL_BILLING_HISTORY } from '~/gql/billing/billing'
import { client } from '~/helpers/apollo'
import BillingHistoryTable from '~/pages/backoffice/admin/billing/BillingHistoryTable'
import BillingProfileForm from '~/pages/backoffice/admin/billing/BillingProfileForm'
import EstimateCards from '~/pages/backoffice/admin/billing/EstimateCards'
import { currentMonth } from '~/pages/backoffice/admin/billing/month'
import MonthPicker from '~/pages/backoffice/admin/billing/MonthPicker'
import PlanCard from '~/pages/backoffice/admin/billing/PlanCard'

/**
 * Onglet de facturation de l'organisation (#51), réservé à l'admin : il est le
 * titulaire du contrat, il gère lui-même sa formule et ses coordonnées.
 *
 * Contrepartie du prix à l'usage : le client voit en continu ce sur quoi il
 * sera facturé. Rien n'est encaissé ici (le paiement en ligne viendra), et le
 * plan gratuit reste un repère affiché — jamais une limite appliquée.
 */
const HISTORY_MONTHS = 12

function AdminBilling() {
  const { t } = useTranslation()
  const [month, setMonth] = useState(() => currentMonth())

  const { data: accountData, refetch: refetchAccount } = useQuery(GQL_BILLING_ACCOUNT, {
    client,
    fetchPolicy: 'network-only',
  })
  const { data: estimateData, refetch: refetchEstimate } = useQuery(GQL_BILLING_ESTIMATE, {
    client,
    variables: { month },
    fetchPolicy: 'network-only',
  })
  const { data: historyData, refetch: refetchHistory } = useQuery(GQL_BILLING_HISTORY, {
    client,
    variables: { months: HISTORY_MONTHS },
    fetchPolicy: 'network-only',
  })
  /**
   * Le choix de formule ne se lit pas dans le mois observé : le palier plancher
   * dépend du compteur d'AUJOURD'HUI. D'où une estimation épinglée au mois
   * courant, que le sélecteur de mois ne déplace pas (Apollo dédoublonne la
   * requête tant que les deux portent sur le même mois).
   */
  const { data: currentData, refetch: refetchCurrent } = useQuery(GQL_BILLING_ESTIMATE, {
    client,
    variables: { month: currentMonth() },
    fetchPolicy: 'network-only',
  })

  const account = accountData?.billingAccount as unknown as BillingAccount | undefined
  const estimate = estimateData?.billingEstimate as unknown as BillingEstimate | undefined
  const currentEstimate = currentData?.billingEstimate as unknown as BillingEstimate | undefined
  const history = (historyData?.billingHistory ?? []) as unknown as BillingHistoryEntry[]

  /**
   * Tout l'écran dépend de la formule, historique compris (les montants passés
   * sont recalculés avec elle) : un changement rejoue les trois requêtes.
   */
  const handlePlanChanged = async () => {
    await Promise.all([refetchAccount(), refetchEstimate(), refetchCurrent(), refetchHistory()])
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">{t('billing.title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('billing.help')}</p>
        </div>
        <MonthPicker value={month} onChange={setMonth} />
      </div>

      <EstimateCards estimate={estimate} />
      <PlanCard account={account} estimate={currentEstimate} onChanged={handlePlanChanged} />
      <BillingProfileForm account={account} />
      <BillingHistoryTable rows={history} />
    </div>
  )
}

export default AdminBilling
